import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

export interface HREmployee {
  id: string
  first_name: string
  last_name: string
  full_name?: string
  work_email?: string
  profile_picture?: string | null
  country_of_work?: string
  role?: string
}

export interface HRMessage {
  id: string
  conversation: string
  sender: HREmployee | null
  sender_id: string | null
  body: string
  message_type: 'message' | 'system'
  created_at: string
  updated_at: string
}

export interface HRReplySuggestionSource {
  file_id?: string | null
  title?: string
  snippet?: string
  score?: number
}

export interface HRReplyToolContext {
  name: string
  label: string
  guidance: string
}

export interface HRStructuredDraft {
  reply_text?: string
  confidence?: 'high' | 'medium' | 'low'
  needs_review?: boolean
  review_reason?: string
  next_steps?: string[]
  tool_context?: HRReplyToolContext[]
}

export interface HRReplySuggestion {
  id: string
  conversation: string
  requester_message_id: string
  assignee: HREmployee
  draft_body: string
  structured_draft?: HRStructuredDraft | null
  status: 'pending' | 'ready' | 'used' | 'rejected' | 'failed'
  sources: HRReplySuggestionSource[]
  error: string
  created_at: string
  updated_at: string
}

export interface HRConversation {
  id: string
  requester: HREmployee
  assignee: HREmployee | null
  country_of_work: string
  status: 'open' | 'closed'
  routing_source: 'country_contact' | 'org_admin_fallback' | 'transfer'
  requester_last_read_at: string | null
  assignee_last_read_at: string | null
  closed_at: string | null
  last_message: {
    id: string
    body: string
    message_type: 'message' | 'system'
    sender_id: string | null
    created_at: string
  } | null
  unread_count: number
  is_own: boolean
  is_assigned: boolean
  created_at: string
  updated_at: string
}

export interface CountryContactRow {
  country: string
  employee_count: number
  contact: HREmployee | null
}

interface ListResponse<T> {
  data?: T[]
  results?: T[]
}

interface EmployeePageResponse {
  data: HREmployee[]
  page: number
  page_size: number
  has_next: boolean
}

const listData = <T,>(payload: ListResponse<T> | T[]): T[] => {
  if (Array.isArray(payload)) return payload
  return payload.data ?? payload.results ?? []
}

export const startHRConversation = async (): Promise<HRConversation> => {
  const response = await api.post('/hr-chat/conversations/start/')
  return response.data
}

export const fetchHRConversations = async (): Promise<HRConversation[]> => {
  const response = await api.get<ListResponse<HRConversation>>('/hr-chat/conversations/')
  return listData(response.data)
}

export const fetchHRMessages = async (conversationId: string): Promise<HRMessage[]> => {
  const response = await api.get<ListResponse<HRMessage>>(`/hr-chat/conversations/${conversationId}/messages/`, {
    params: { page_size: 200 },
  })
  return listData(response.data)
}

export const sendHRMessage = async ({ conversationId, body }: { conversationId: string; body: string }): Promise<HRMessage> => {
  const response = await api.post(`/hr-chat/conversations/${conversationId}/messages/`, { body })
  return response.data
}

export const markHRConversationRead = async (conversationId: string): Promise<void> => {
  await api.post(`/hr-chat/conversations/${conversationId}/mark-read/`)
}

export const transferHRConversation = async ({
  conversationId,
  employeeId,
}: {
  conversationId: string
  employeeId: string
}): Promise<HRConversation> => {
  const response = await api.post(`/hr-chat/conversations/${conversationId}/transfer/`, {
    employee_id: employeeId,
  })
  return response.data
}

export const fetchCountryContactRows = async (): Promise<CountryContactRow[]> => {
  const response = await api.get<ListResponse<CountryContactRow>>('/hr-chat/contact-routes/')
  return listData(response.data)
}

export const setCountryContact = async ({
  country,
  employeeId,
}: {
  country: string
  employeeId: string | null
}): Promise<CountryContactRow[]> => {
  const response = await api.patch<ListResponse<CountryContactRow>>('/hr-chat/contact-routes/set-contact/', {
    country,
    employee_id: employeeId,
  })
  return listData(response.data)
}

export const fetchHREligibleEmployees = async (): Promise<HREmployee[]> => {
  const response = await api.get<ListResponse<HREmployee>>('/hr-chat/contact-routes/eligible-employees/')
  return listData(response.data)
}

export const fetchHRTransferTargets = async (): Promise<HREmployee[]> => {
  const response = await api.get<ListResponse<HREmployee>>('/hr-chat/conversations/transfer-targets/')
  return listData(response.data)
}

export const fetchHREligibleEmployeesPage = async ({
  search,
  page,
  pageSize = 20,
}: {
  search: string
  page: number
  pageSize?: number
}): Promise<EmployeePageResponse> => {
  const response = await api.get<EmployeePageResponse>('/hr-chat/contact-routes/eligible-employees/', {
    params: { search, page, page_size: pageSize },
  })
  return response.data
}

export const fetchHRTransferTargetsPage = async ({
  search,
  page,
  pageSize = 20,
}: {
  search: string
  page: number
  pageSize?: number
}): Promise<EmployeePageResponse> => {
  const response = await api.get<EmployeePageResponse>('/hr-chat/conversations/transfer-targets/', {
    params: { search, page, page_size: pageSize },
  })
  return response.data
}

export const fetchHRReplySuggestions = async (conversationId: string): Promise<HRReplySuggestion[]> => {
  const response = await api.get<ListResponse<HRReplySuggestion>>(`/hr-chat/conversations/${conversationId}/reply-suggestions/`)
  return listData(response.data)
}

export const generateHRReplySuggestion = async ({
  conversationId,
  messageId,
}: {
  conversationId: string
  messageId: string
}): Promise<HRReplySuggestion> => {
  const response = await api.post(
    `/hr-chat/conversations/${conversationId}/messages/${messageId}/reply-suggestions/generate/`,
    {},
    { timeout: 120000 },
  )
  return response.data
}

export const markHRReplySuggestionUsed = async (suggestionId: string): Promise<HRReplySuggestion> => {
  const response = await api.post(`/hr-chat/reply-suggestions/${suggestionId}/use/`)
  return response.data
}

export const rejectHRReplySuggestion = async (suggestionId: string): Promise<HRReplySuggestion> => {
  const response = await api.post(`/hr-chat/reply-suggestions/${suggestionId}/reject/`)
  return response.data
}

export const useHRConversations = (enabled: boolean) =>
  useQuery({
    queryKey: ['hr-chat', 'conversations'],
    queryFn: fetchHRConversations,
    enabled,
    refetchInterval: enabled ? 15000 : false,
  })

export const useHRMessages = (conversationId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['hr-chat', 'messages', conversationId],
    queryFn: () => fetchHRMessages(conversationId!),
    enabled: enabled && !!conversationId,
    refetchInterval: enabled && conversationId ? 10000 : false,
  })

export const useStartHRConversation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startHRConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'conversations'] })
    },
  })
}

export const useSendHRMessage = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: sendHRMessage,
    onSuccess: (message, variables) => {
      queryClient.setQueryData<HRMessage[]>(['hr-chat', 'messages', variables.conversationId], (current = []) => {
        if (current.some((item) => item.id === message.id)) return current
        return [...current, message]
      })
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'conversations'] })
    },
  })
}

export const useMarkHRConversationRead = () => (
  useMutation({
    mutationFn: markHRConversationRead,
  })
)

export const useTransferHRConversation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: transferHRConversation,
    onSuccess: (_conversation, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'messages', variables.conversationId] })
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'conversations'] })
    },
  })
}

export const useCountryContactRows = (enabled: boolean) =>
  useQuery({
    queryKey: ['hr-chat', 'country-contacts'],
    queryFn: fetchCountryContactRows,
    enabled,
  })

export const useHREligibleEmployees = (enabled: boolean) =>
  useQuery({
    queryKey: ['hr-chat', 'eligible-employees'],
    queryFn: fetchHREligibleEmployees,
    enabled,
    staleTime: 60 * 1000,
  })

export const useHRTransferTargets = (enabled: boolean) =>
  useQuery({
    queryKey: ['hr-chat', 'transfer-targets'],
    queryFn: fetchHRTransferTargets,
    enabled,
    staleTime: 60 * 1000,
  })

export const useHREligibleEmployeesInfinite = (enabled: boolean, search: string) =>
  useInfiniteQuery({
    queryKey: ['hr-chat', 'eligible-employees', search],
    queryFn: ({ pageParam = 1 }) => fetchHREligibleEmployeesPage({ search, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.has_next ? lastPage.page + 1 : undefined),
    enabled,
    staleTime: 60 * 1000,
  })

export const useHRTransferTargetsInfinite = (enabled: boolean, search: string) =>
  useInfiniteQuery({
    queryKey: ['hr-chat', 'transfer-targets', search],
    queryFn: ({ pageParam = 1 }) => fetchHRTransferTargetsPage({ search, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.has_next ? lastPage.page + 1 : undefined),
    enabled,
    staleTime: 60 * 1000,
  })

export const useHRReplySuggestions = (conversationId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['hr-chat', 'reply-suggestions', conversationId],
    queryFn: () => fetchHRReplySuggestions(conversationId!),
    enabled: enabled && !!conversationId,
    refetchInterval: enabled && conversationId ? 15000 : false,
  })

export const useGenerateHRReplySuggestion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: generateHRReplySuggestion,
    onSuccess: (suggestion, variables) => {
      queryClient.setQueryData<HRReplySuggestion[]>(['hr-chat', 'reply-suggestions', variables.conversationId], (current = []) => {
        const next = current.filter((item) => item.id !== suggestion.id && item.requester_message_id !== suggestion.requester_message_id)
        return [...next, suggestion]
      })
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'reply-suggestions', variables.conversationId] })
    },
  })
}

export const useUseHRReplySuggestion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: markHRReplySuggestionUsed,
    onSuccess: (suggestion) => {
      queryClient.setQueryData<HRReplySuggestion[]>(['hr-chat', 'reply-suggestions', suggestion.conversation], (current = []) => (
        current.map((item) => (item.id === suggestion.id ? suggestion : item))
      ))
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'reply-suggestions', suggestion.conversation] })
    },
  })
}

export const useRejectHRReplySuggestion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: rejectHRReplySuggestion,
    onSuccess: (suggestion) => {
      queryClient.setQueryData<HRReplySuggestion[]>(['hr-chat', 'reply-suggestions', suggestion.conversation], (current = []) => (
        current.map((item) => (item.id === suggestion.id ? suggestion : item))
      ))
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'reply-suggestions', suggestion.conversation] })
    },
  })
}

export const useSetCountryContact = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setCountryContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'country-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['hr-chat', 'conversations'] })
    },
  })
}
